/**
 * United States federal holiday calendar for business operations and scheduling.
 * Used by the barbershop rental application to determine business hours, 
 * availability calculations, and administrative office operations.
 * 
 * Each holiday entry contains scheduling metadata:
 * - Fixed dates use month/day properties
 * - Floating holidays use weekday/occurrence properties 
 * - Office availability is specified for administrative functions
 * 
 * @constant {object} HOLIDAYS_US - US holiday configuration object
 * @description Holiday calendar for business scheduling and availability calculations
 * 
 * @property {object} "New Year's Day" - January 1st, office closed
 * @property {object} "Martin Luther King Jr. Day" - Third Monday in January, office closed
 * @property {object} "Presidents' Day" - Third Monday in February, office closed
 * @property {object} "Memorial Day" - Last Monday in May, office closed
 * @property {object} "Juneteenth National Independence Day" - June 19th, office closed
 * @property {object} "Independence Day" - July 4th, office closed
 * @property {object} "Labor Day" - First Monday in September, office closed
 * @property {object} "Columbus Day" - Second Monday in October, office open
 * @property {object} "Veterans Day" - November 11th, office closed
 * @property {object} "Thanksgiving Day" - Fourth Thursday in November, office closed
 * @property {object} "Day after Thanksgiving" - Friday after Thanksgiving, office open
 * @property {object} "Christmas Day" - December 25th, office closed
 * 
 * Each holiday object structure:
 * @property {number|null} month - Month number (1-12) for fixed dates
 * @property {number|null} day - Day number for fixed dates
 * @property {number|null} weekday - Day of week (0=Sunday, 1=Monday, etc.) for floating holidays
 * @property {number|null} occurrence - Week occurrence (1=first, 2=second, 3=third, 4=fourth, -1=last)
 * @property {boolean} officeOpen - Whether administrative office operates on this day
 */
import { HolidayInfo } from '../@models/holiday-info';

export const HOLIDAYS_US: Record<string, HolidayInfo> = {
    "HOLIDAYS_US.NEW_YEAR": { month: 1, day: 1, weekday: null, occurrence: null, officeOpen: false },
    "HOLIDAYS_US.MLK_DAY": { month: 1, day: null, weekday: 1, occurrence: 3, officeOpen: false }, // Third Monday in January
    "HOLIDAYS_US.PRESIDENTS_DAY": { month: 2, day: null, weekday: 1, occurrence: 3, officeOpen: false }, // Third Monday in February
    "HOLIDAYS_US.MEMORIAL_DAY": { month: 5, day: null, weekday: 1, occurrence: -1, officeOpen: false }, // Last Monday in May
    "HOLIDAYS_US.JUNETEENTH": { month: 6, day: 19, weekday: null, occurrence: null, officeOpen: false },
    "HOLIDAYS_US.INDEPENDENCE_DAY": { month: 7, day: 4, weekday: null, occurrence: null, officeOpen: false },
    "HOLIDAYS_US.LABOR_DAY": { month: 9, day: null, weekday: 1, occurrence: 1, officeOpen: false }, // First Monday in September
    "HOLIDAYS_US.COLUMBUS_DAY": { month: 10, day: null, weekday: 1, occurrence: 2, officeOpen: true }, // Second Monday in October
    "HOLIDAYS_US.VETERANS_DAY": { month: 11, day: 11, weekday: null, occurrence: null, officeOpen: false },
    "HOLIDAYS_US.THANKSGIVING": { month: 11, day: null, weekday: 4, occurrence: 4, officeOpen: false }, // Fourth Thursday in November
    "HOLIDAYS_US.DAY_AFTER_THANKSGIVING": { month: 11, day: null, weekday: 5, occurrence: 4, officeOpen: true }, // Fourth Friday in November
    "HOLIDAYS_US.CHRISTMAS": { month: 12, day: 25, weekday: null, occurrence: null, officeOpen: false },
};